import { VoidCallback } from '@deqo/foundation';
import { Placement } from '@floating-ui/react-dom';
import { Composition } from '../../../../concepts/revive/Composition';
import { StoreWithUpdate } from '../../../../concepts/revive/Store';
import { Point } from '../../../../platform/geom/Point';
import { Rect } from '../../../../platform/geom/Rect';

export type WidgetContainerDockingPoints =
    'topLeft' | 'top' | 'topRight' | 'right' | 'bottomRight' | 'bottom' | 'bottomLeft' | 'left';
export const WidgetContainerDockingPoints =
    ['topLeft', 'top', 'topRight', 'right', 'bottomRight', 'bottom', 'bottomLeft', 'left'] as WidgetContainerDockingPoints[];

export type WidgetContainerDockingModes = 'inline' | 'stack';
export type WidgetContainerStyle = 'float' | 'floatHover' | 'floatWithLines' | 'none';

export type WidgetContainerAttachedPositionConfig = {
    to: string | string[] | Rect,
    placement: Placement,
    offset?: [number, number],
    zOrder?: number,
};

export type WidgetContainerRelativePositionConfig = {
    to: string | string[],
    at: Point,
};

export type WidgetContainerAbsolutePositionConfig = {
    at: Point,
    relativeTo: 'model' | 'view',
    zOrder?: number,
};

export type WidgetContainerPosition =
    ['docked', WidgetContainerDockingPoints, WidgetContainerDockingModes?] |
    ['attached', WidgetContainerAttachedPositionConfig] |
    ['relative', WidgetContainerRelativePositionConfig] |
    ['absolute', WidgetContainerAbsolutePositionConfig] |
    ['unmanaged'];

export interface WidgetContainerProps {
    name: string;
    tag: string;
    position: WidgetContainerPosition;
    direction: 'column' | 'row';
    style: WidgetContainerStyle;
    // padding: string | null;
    scale: number;
    weight: number | null;
    data: Record<string, unknown>;
}

export interface WidgetContainer extends Composition<Widget>, WidgetContainerProps {
    readonly id: string;
    get<T extends Widget<any>>(name: string): T | null;
}

export type WidgetBaseProps = {
    name: string | null,
    tag: string | null,
    width: number | null,
    height: number | null,
    active: boolean,
    enabled: boolean,
    visible: boolean,
    weight: number | null,
};

export type ButtonLikeWidgetProps = {
    image: string | null,
    text: string | null,
};

export type ActionWidgetProps = {
    onAction: VoidCallback,
};

export type PopperWidgetProps = {
    open: boolean,
    onOpen: VoidCallback,
    onClose: VoidCallback,
};

export type SelectorWidgetProps<T = string | null> = PopperWidgetProps & {
    value: T,
    onSelect: (this: SelectorWidgetProps<T>) => void,
};

export type Widget<T extends WidgetBaseProps = any> = StoreWithUpdate<T> & Readonly<T> & {
    readonly id: string,
    readonly type: string,
};

type DefineWidget<T> = Widget<T & WidgetBaseProps>;


export type ButtonWidget = DefineWidget<ButtonLikeWidgetProps & ActionWidgetProps>;

export type ButtonStripWidget = DefineWidget<ButtonLikeWidgetProps & PopperWidgetProps & {
    items: readonly ButtonWidget[],
    layout: string,
}>;

export type ColorSelectorWidget = DefineWidget<ButtonLikeWidgetProps & SelectorWidgetProps & {
    allowNull: boolean,
    autoSelect: boolean,
    groups: boolean,
}>;

export type DivideWidget = DefineWidget<{}>;

export type EmojiSelectorWidget = DefineWidget<ButtonLikeWidgetProps & SelectorWidgetProps>;

export type InlineWidget = DefineWidget<{
    ui: any | null,
    [key: string]: any,
}>;

export type MenuWidget = DefineWidget<ButtonLikeWidgetProps & PopperWidgetProps & {
    items: readonly ButtonWidget[],
    itemsLayout: string,
    itemsWidth: number | null,
    padding: number | null,
}>;

export type PopoutWidget = DefineWidget<ButtonLikeWidgetProps & SelectorWidgetProps<any> & {
    ui: any | null,
    placement: string | null,
    autoClose: boolean,
    [key: string]: any,
}>;

export type SliderWidget = DefineWidget<ButtonLikeWidgetProps & SelectorWidgetProps<number> & {
    min: number | null,
    max: number | null,
}>;

export type TextInputWidget = DefineWidget<ButtonLikeWidgetProps & SelectorWidgetProps>;

export const widgetSorter = (a: any, b: any) => {
    if (a.weight === b.weight) return 0;
    if (a.weight === null) return 1;
    if (b.weight === null) return -1;
    return a.weight - b.weight;
};
